/* PPE Parts & Supply: slide-out cart.
   <cart-drawer id="CartDrawer" data-section-id data-error [data-disabled]>
     #CartDrawerContent  replaced from the Section Rendering API after each change
     [data-drawer-count] item count in the heading (outside the replaced content)
     li[data-drawer-line][data-key] > input[data-drawer-qty], a[data-drawer-remove], [data-line-error]
     [data-drawer-close] overlay and close button
   Opened by any [data-cart-drawer-toggle] (the header cart link) and by theme.js after an
   add to cart (openAfterAdd). Listens for cart:updated so changes made elsewhere show here. */
(function () {
  'use strict';

  if (customElements.get('cart-drawer')) return;

  const theme = () => window.theme || {};
  const routes = () => theme().routes || {};
  const cssEscape = (value) => (window.CSS && CSS.escape ? CSS.escape(value) : String(value).replace(/"/g, '\\"'));
  const rootUrl = () => {
    const root = routes().root || '/';
    return root.endsWith('/') ? root : `${root}/`;
  };

  class CartDrawer extends HTMLElement {
    connectedCallback() {
      if (this.dataset.bound) return;
      this.dataset.bound = 'true';

      this.sectionId = this.dataset.sectionId;
      this.queue = Promise.resolve();
      this.timers = {};
      this.ticket = 0;
      this.panel = this.querySelector('.cart-drawer__panel');

      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-drawer-close]')) {
          event.preventDefault();
          this.close();
          return;
        }
        const add = event.target.closest('[data-recs-add]');
        if (add) {
          event.preventDefault();
          this.addRecommendation(add);
          return;
        }
        const remove = event.target.closest('[data-drawer-remove]');
        if (!remove) return;
        event.preventDefault();
        const line = remove.closest('[data-drawer-line]');
        if (line) this.enqueue(line.dataset.key, 0, 'remove');
      });

      this.addEventListener('change', (event) => {
        const input = event.target.closest('[data-drawer-qty]');
        const line = input && input.closest('[data-drawer-line]');
        if (!line) return;
        const key = line.dataset.key;
        clearTimeout(this.timers[key]);
        this.timers[key] = setTimeout(() => this.enqueue(key, null, 'input'), 300);
      });

      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          this.close();
          return;
        }
        if (event.key === 'Tab') {
          this.trapFocus(event);
          return;
        }
        if (event.key !== 'Enter') return;
        const input = event.target.closest('[data-drawer-qty]');
        const line = input && input.closest('[data-drawer-line]');
        if (!line) return;
        event.preventDefault();
        clearTimeout(this.timers[line.dataset.key]);
        this.enqueue(line.dataset.key, null, 'input');
      });

      this.onToggle = (event) => {
        const toggle = event.target.closest('[data-cart-drawer-toggle]');
        if (!toggle || this.disabled) return;
        // Let modified clicks open the cart page in a new tab as usual.
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
        event.preventDefault();
        this.open(toggle);
      };
      document.addEventListener('click', this.onToggle);

      this.onCartUpdated = () => {
        if (!this.selfUpdate) this.refresh();
      };
      document.addEventListener('cart:updated', this.onCartUpdated);

      this.setupSwipe();
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onToggle);
      document.removeEventListener('cart:updated', this.onCartUpdated);
      delete this.dataset.bound;
    }

    get disabled() {
      return this.hasAttribute('data-disabled');
    }

    get isOpen() {
      return this.classList.contains('is-open');
    }

    /* ---------------- Open & close ---------------- */

    open(trigger) {
      if (this.disabled) return;
      if (!this.isOpen) {
        this.trigger = trigger || document.activeElement;
        this.classList.add('is-open');
        document.body.classList.add('overflow-hidden');
        document.querySelectorAll('[data-cart-drawer-toggle]').forEach((el) => el.setAttribute('aria-expanded', 'true'));
      }
      const close = this.querySelector('.drawer__close');
      if (close) requestAnimationFrame(() => close.focus());
      this.loadRecs();
    }

    close() {
      if (!this.isOpen) return;
      this.classList.remove('is-open');
      document.body.classList.remove('overflow-hidden');
      document.querySelectorAll('[data-cart-drawer-toggle]').forEach((el) => el.setAttribute('aria-expanded', 'false'));
      if (this.trigger && this.trigger.isConnected) this.trigger.focus();
    }

    /* Touch: drag the panel to the right to close it. Only a mostly horizontal,
       rightward drag is taken over; anything else is left to scroll the cart. */
    setupSwipe() {
      const panel = this.panel;
      const overlay = this.querySelector('.drawer__overlay');
      if (!panel) return;
      let startX = 0;
      let startY = 0;
      let startTime = 0;
      let dx = 0;
      let tracking = false;
      let dragging = false;

      const reset = () => {
        panel.style.transition = '';
        panel.style.transform = '';
        if (overlay) {
          overlay.style.transition = '';
          overlay.style.opacity = '';
        }
      };

      panel.addEventListener('touchstart', (event) => {
        if (event.touches.length !== 1) return;
        startX = event.touches[0].clientX;
        startY = event.touches[0].clientY;
        startTime = Date.now();
        dx = 0;
        tracking = true;
        dragging = false;
      }, { passive: true });

      panel.addEventListener('touchmove', (event) => {
        if (!tracking) return;
        const moveX = event.touches[0].clientX - startX;
        const moveY = event.touches[0].clientY - startY;
        if (!dragging) {
          if (Math.abs(moveX) < 10 && Math.abs(moveY) < 10) return;
          if (moveX <= 0 || Math.abs(moveY) > Math.abs(moveX)) {
            tracking = false;
            return;
          }
          dragging = true;
        }
        dx = Math.max(0, moveX);
        panel.style.transition = 'none';
        panel.style.transform = `translateX(${dx}px)`;
        if (overlay) overlay.style.opacity = String(Math.max(0, 1 - dx / panel.offsetWidth));
      }, { passive: true });

      const end = () => {
        if (!tracking || !dragging) {
          tracking = false;
          return;
        }
        tracking = false;
        dragging = false;
        const flick = dx > 40 && dx / Math.max(1, Date.now() - startTime) > 0.5;
        const ease = 'cubic-bezier(.2, .7, .2, 1)';
        panel.style.transition = `transform .22s ${ease}`;
        if (overlay) overlay.style.transition = `opacity .22s ${ease}`;
        if (flick || dx > panel.offsetWidth * 0.3) {
          panel.style.transform = 'translateX(100%)';
          if (overlay) overlay.style.opacity = '0';
          setTimeout(() => {
            this.close();
            reset();
          }, 220);
        } else {
          panel.style.transform = '';
          if (overlay) overlay.style.opacity = '';
          setTimeout(reset, 220);
        }
      };
      panel.addEventListener('touchend', end);
      panel.addEventListener('touchcancel', end);
    }

    /* After an add to cart: show the drawer at once and fill it as soon as the cart is fetched. */
    openAfterAdd() {
      this.open(document.activeElement);
      return this.refresh();
    }

    trapFocus(event) {
      const focusable = Array.from(
        this.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), [tabindex="0"]')
      ).filter((el) => el.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    /* ---------------- Rendering ---------------- */

    async refresh() {
      const ticket = ++this.ticket;
      try {
        const response = await fetch(`${rootUrl()}?sections=${encodeURIComponent(this.sectionId)}`);
        const data = await response.json();
        if (ticket === this.ticket && data[this.sectionId]) this.swap(data[this.sectionId]);
      } catch (error) {
        /* offline or aborted: keep what is shown */
      }
    }

    swap(html) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.getElementById('CartDrawerContent');
      const current = this.querySelector('#CartDrawerContent');
      if (fresh && current) current.innerHTML = fresh.innerHTML;
      if (fresh && current) {
        Array.from(current.attributes).forEach((attr) => {
          if (attr.name.startsWith('data-') && !fresh.hasAttribute(attr.name)) current.removeAttribute(attr.name);
        });
        Array.from(fresh.attributes).forEach((attr) => {
          if (attr.name.startsWith('data-')) current.setAttribute(attr.name, attr.value);
        });
      }
      const freshCount = doc.querySelector('[data-drawer-count]');
      const count = this.querySelector('[data-drawer-count]');
      if (freshCount && count) count.innerHTML = freshCount.innerHTML;
      if (this.isOpen) this.loadRecs();
    }

    /* ---------------- You might also like ---------------- */

    /* Rows for the first part in the cart, cached per cart contents so each change
       costs at most one request. The replaced content brings an empty container back,
       so cached rows are put straight back in. */
    async loadRecs() {
      if (this.hasAttribute('data-no-recs')) return;
      const content = this.querySelector('#CartDrawerContent');
      const container = this.querySelector('[data-drawer-recs]');
      if (!content || !container || !content.dataset.firstProduct) return;

      const key = `${content.dataset.firstProduct}|${content.dataset.cartProducts || ''}`;
      this.recsCache = this.recsCache || {};
      if (!(key in this.recsCache)) {
        try {
          const response = await fetch(`${this.dataset.recsUrl}&product_id=${encodeURIComponent(content.dataset.firstProduct)}`);
          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          const root = doc.querySelector('[data-recs-root]');
          this.recsCache[key] = root ? root.innerHTML.trim() : '';
        } catch (error) {
          return;
        }
      }

      // The drawer may have been re-rendered while the request was out.
      const live = this.querySelector('[data-drawer-recs]');
      const liveContent = this.querySelector('#CartDrawerContent');
      if (!live || !liveContent) return;
      if (`${liveContent.dataset.firstProduct}|${liveContent.dataset.cartProducts || ''}` !== key) return;
      live.innerHTML = this.recsCache[key];
      live.hidden = !this.recsCache[key];
    }

    async addRecommendation(button) {
      if (button.getAttribute('aria-busy') === 'true') return;
      button.setAttribute('aria-busy', 'true');
      button.disabled = true;
      try {
        const response = await fetch(routes().cartAdd, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(button.dataset.recsAdd), quantity: 1 }] })
        });
        const data = await response.json();
        if (!response.ok || data.status) throw new Error(data.description || data.message || this.dataset.error);
        // cart:updated re-renders the drawer, which drops the added part from the suggestions.
        if (theme().cart) await theme().cart.refresh();
        else await this.refresh();
      } catch (error) {
        button.removeAttribute('aria-busy');
        button.disabled = false;
        if (theme().announce) theme().announce(error.message || this.dataset.error);
      }
    }

    /* ---------------- Quantity & remove ---------------- */

    findLine(key) {
      return this.querySelector(`[data-drawer-line][data-key="${cssEscape(key)}"]`);
    }

    enqueue(key, quantity, focusTarget) {
      this.queue = this.queue.then(() => this.updateLine(key, quantity, focusTarget)).catch(() => {});
    }

    async updateLine(key, requested, focusTarget) {
      const lineEl = this.findLine(key);
      if (!lineEl) return;
      const input = lineEl.querySelector('[data-drawer-qty]');

      let quantity = requested;
      if (quantity === null) {
        quantity = parseInt(input ? input.value : '', 10);
        if (Number.isNaN(quantity) || quantity < 0) {
          if (input) input.value = input.defaultValue;
          return;
        }
        if (input && String(quantity) === input.defaultValue) return;
      }

      const hadFocus = this.contains(document.activeElement);
      this.setLoading(lineEl, true);
      this.showError(lineEl, '');
      this.ticket += 1; // a refresh already in flight must not overwrite this result

      try {
        const response = await fetch(routes().cartChange, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: key, quantity, sections: this.sectionId, sections_url: window.location.pathname })
        });
        const data = await response.json();

        if (!response.ok || data.status || data.errors) {
          const message = data.description || data.message || (typeof data.errors === 'string' ? data.errors : '') || this.dataset.error;
          if (input) input.value = input.defaultValue;
          this.setLoading(lineEl, false);
          this.showError(lineEl, message);
          return;
        }

        const html = data.sections && data.sections[this.sectionId];
        if (html) this.swap(html);
        else await this.refresh();
        if (hadFocus) this.restoreFocus(key, focusTarget);

        // Header count and total, and the cart page if it is open in this tab.
        this.selfUpdate = true;
        try {
          if (theme().cart) theme().cart.render(data);
        } finally {
          this.selfUpdate = false;
        }
      } catch (error) {
        if (input) input.value = input.defaultValue;
        this.setLoading(lineEl, false);
        this.showError(lineEl, this.dataset.error);
      }
    }

    restoreFocus(key, focusTarget) {
      const lineEl = this.findLine(key);
      const target = lineEl && lineEl.querySelector(focusTarget === 'remove' ? '[data-drawer-remove]' : '[data-drawer-qty]');
      const fallback = this.querySelector('[data-drawer-qty]') || this.querySelector('.drawer__close');
      const el = target || fallback;
      if (el) el.focus();
    }

    setLoading(lineEl, loading) {
      lineEl.classList.toggle('is-loading', loading);
      if (loading) lineEl.setAttribute('aria-busy', 'true');
      else lineEl.removeAttribute('aria-busy');
    }

    showError(lineEl, message) {
      const error = lineEl.querySelector('[data-line-error]');
      if (!error) return;
      error.textContent = message || '';
      error.hidden = !message;
    }
  }

  customElements.define('cart-drawer', CartDrawer);
})();
